import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  LOCALE_ID,
  inject,
} from '@angular/core';
import {
  CommonService,
  ServiceCategoryService,
  TaxGroupService,
} from '@fitness-force/api';
import { NotFoundError } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type { Country, TENANTSINTERFACE } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COUNTRY_MASTER_STATE, TENANTS_INFO } from '@fitness-force/state';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';

import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogModule,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { getCurrencySymbol, NgClass } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  FormControl,
  AbstractControl,
  UntypedFormControl,
} from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  MembershipBehaviour,
  PlanCategory,
  PlanIntervalType,
  PlanType,
} from '@fitness-force/enum';
import { distinctUntilChanged, forkJoin, Observable } from 'rxjs';
import { PlanCategoryBehaviour, Status } from '@fitness-force/enum';
import { ServiceCategoryCreateComponent } from '../service-category-create/service-category-create.component';
import { MessageModule } from 'primeng/message';

import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberComponent } from '../input-number/input-number.component';

@Component({
  selector: 'plan-service-modal',
  templateUrl: './plan-service-modal.component.html',
  styleUrls: ['./plan-service-modal.component.scss'],
  providers: [ToastMessageService, DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    RippleModule,
    DialogModule,
    DividerModule,
    PopoverModule,
    TranslateModule,
    ErrorMsgComponent,
    DynamicDialogModule,
    InputNumberModule,
    SelectModule,
    ToggleSwitchModule,
    ServiceCategoryCreateComponent,
    MessageModule,
    InputNumberComponent,
    RadioButtonModule,
    NgClass,
  ],
})
export class PlanServiceModalComponent implements OnInit {
  private store = inject(Store);
  private serviceCategoryService = inject(ServiceCategoryService);
  private taxGroupService = inject(TaxGroupService);
  private toastMessageService = inject(ToastMessageService);
  private translate = inject(TranslateService);
  private ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private fb = inject(UntypedFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private commonService = inject(CommonService);
  locale = inject(LOCALE_ID);

  getAccessTenantList$: Observable<any> = new Observable();
  tenantId: number = 0;
  name_primary_language!: string;

  activeEditService!: any;
  activeEditIndex!: number;
  serviceCreateForm: UntypedFormGroup;
  membershipBehaviour: any[] = [];
  services: any[] = [];
  taxGroup: any = [];
  currencySymbol: any;
  subscriptionCategory!: any;
  addCategory = false;
  onlyClass: boolean;
  planType!: any;
  noTax: any;
  planId: number;
  isSecondaryLanguage = false;
  primaryLanguage!: string;
  secondaryLanguage!: string;
  planIntervalType: any[] = [];
  mode: string = 'plans';
  type: string = 'single';

  constructor() {
    if (this.config.data?.service) {
      this.setBaseMembershipLogicOnEdit();
      this.activeEditService = this.config.data?.service;
      this.activeEditIndex = this.config.data?.index;
    }
    if (this.config.data?.planId) {
      this.planId = this.config.data?.planId;
    }
    if (this.config.data?.mode) {
      this.type = this.config.data?.type;
    }

    if (this.config.data?.type) {
      this.type = this.config.data?.type;
    }

    if (this.config.data?.subscriptionCategory) {
      this.subscriptionCategory = this.config.data?.subscriptionCategory;
    }
    if (this.config.data?.planType) {
      this.planType = this.config.data?.planType;
    }
    this.planIntervalType = Object.keys(PlanIntervalType)
      .filter((key) => !Number.isNaN(Number(key)))
      .map((key: any) => PlanIntervalType[key]);
  }

  ngOnInit(): void {
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.store.dispatch({ type: 'ROUTER_GO' });
      }
      this.translate
        .get([
          'common.LANGUAGE.' +
            tenant.selected_tenant.primary_language?.toUpperCase(),
          'common.LANGUAGE.' +
            tenant.selected_tenant.secondary_language?.toUpperCase(),
        ])
        .subscribe((translations) => {
          this.primaryLanguage =
            translations[
              'common.LANGUAGE.' +
                tenant.selected_tenant.primary_language?.toUpperCase()
            ];
          if (tenant.selected_tenant?.secondary_language) {
            this.isSecondaryLanguage = true;
            this.secondaryLanguage =
              translations[
                'common.LANGUAGE.' +
                  tenant.selected_tenant.secondary_language?.toUpperCase()
              ];
          }
        });
      this.preFormData();
    });
    // Could be moved to any Facade service
    this.store.select(COUNTRY_MASTER_STATE).subscribe((country: Country) => {
      this.currencySymbol = getCurrencySymbol(
        country.currency_code,
        'narrow',
        this.locale,
      );
    });

    this.translate.get('ADMIN.SUBSCRIPTION.NOTAX').subscribe((translations) => {
      this.noTax = {
        id: 0,
        name_primary_language: translations,
        name_secondary_language: translations,
      };
    });

    this.membershipBehaviour = Object.keys(MembershipBehaviour).map((key) => {
      return {
        key,
        name: MembershipBehaviour[key as keyof typeof MembershipBehaviour],
      };
    });
  }

  preFormData() {
    let forkArray = [
      this.type == 'single'
        ? this.serviceCategoryService.getServiceCategories(this.tenantId)
        : this.serviceCategoryService.getAggregatedServiceCategories(),
      this.taxGroupService.getTaxGroups(this.tenantId),
    ];

    forkJoin(forkArray).subscribe({
      next: (results: any) => {
        if (this.type == 'single') {
          this.services = results[0]?.items
            ? results[0]?.items?.filter(
                (value: any) =>
                  value.status == Status.Active.toString() ||
                  value.status == Status[Status.Active],
              )
            : [];
          if (
            this.planType &&
            (this.planType == PlanCategory.DropIn ||
              this.planType == PlanCategory[PlanCategory.DropIn])
          ) {
            this.services = this.services.filter((value: any) => {
              return (
                (value?.behaviour == PlanCategoryBehaviour.ClassPackage ||
                  value?.behaviour ==
                    PlanCategoryBehaviour[
                      PlanCategoryBehaviour.ClassPackage
                    ]) &&
                (value.status == Status.Active ||
                  value.status == Status[Status.Active])
              );
            });
          }
        } else {
          this.services = results[0] ? results[0] : [];
          if (
            this.planType &&
            (this.planType == PlanCategory.DropIn ||
              this.planType == PlanCategory[PlanCategory.DropIn])
          ) {
            this.services = this.services.filter((value: any) => {
              return (
                value?.category_behaviour ==
                  PlanCategoryBehaviour.ClassPackage ||
                value?.category_behaviour ==
                  PlanCategoryBehaviour[PlanCategoryBehaviour.ClassPackage]
              );
            });
            this.services = this.services.map((value: any) => {
              return {
                name_primary_language: value.category_name,
                behaviour: value.category_behaviour,
              };
            });
          } else {
            this.services = this.services.map((value: any) => {
              return {
                name_primary_language: value.category_name,
                behaviour: value.category_behaviour,
              };
            });
          }
        }
        if (results[1] && results[1].items) {
          this.taxGroup = results[1].items;
        } else this.taxGroup = [];
        this.taxGroup.unshift(this.noTax);
        this.buildForm();
      },
      error: (error: any) => {
        this.handleError(error);
      },
    });
  }

  reloadServices(id: number) {
    this.serviceCategoryService.getServiceCategories(this.tenantId).subscribe({
      next: (res: any) => {
        this.services = res.items;
        if (
          this.planType &&
          (this.planType == PlanCategory.DropIn ||
            this.planType == PlanCategory[PlanCategory.DropIn])
        ) {
          this.services = this.services.filter((value: any) => {
            return (
              (value?.behaviour == PlanCategoryBehaviour.ClassPackage ||
                value?.behaviour ==
                  PlanCategoryBehaviour[PlanCategoryBehaviour.ClassPackage]) &&
              (value.status == Status.Active ||
                value.status == Status[Status.Active])
            );
          });
        }
        let createdCategory = this.services.find((item: any) => item.id == id);
        this.CategoryId?.setValue(createdCategory);
        this.cd.detectChanges();
      },
      error: (error: any) => {},
    });
  }

  handleError(error: any) {
    if (error instanceof NotFoundError) {
      this.translate
        .get([
          'common.errors.SERVER_SIDE.ERROR_OCCURRED',
          'common.errors.SERVER_SIDE.PAGE_NOT_FOUND',
        ])
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    } else {
      this.translate
        .get([
          'common.errors.SERVER_SIDE.ERROR_OCCURRED',
          'common.errors.SERVER_SIDE.SOMETHING_WRONG',
        ])
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    }
  }

  buildForm() {
    this.serviceCreateForm = this.fb.group({
      base_cost_in_minor_currency: [
        this.activeEditService &&
        this.activeEditService.base_cost_in_minor_currency
          ? this.commonService.ConvertAmountFromMinorCurrency(
              this.activeEditService.base_cost_in_minor_currency,
            )
          : '',
        this.type == 'single' ? Validators.required : null,
      ],
      category_id: [this.initializeCategoryIdForm(), Validators.required],
      taxgrp_id: [
        this.initializeTaxGroupIdForm(),
        this.type == 'single' ? Validators.required : null,
      ],
      tax_item_code: [
        this.activeEditService && this.activeEditService.tax_item_code
          ? this.activeEditService.tax_item_code
          : '',
      ],
      gl_code: [
        this.activeEditService && this.activeEditService.gl_code
          ? this.activeEditService.gl_code
          : '',
      ],
      require_base_membership: [
        this.activeEditService && this.activeEditService.require_base_membership
          ? this.activeEditService.require_base_membership
          : false,
        Validators.required,
      ],
      is_session_owner_required: [
        this.activeEditService &&
        this.activeEditService.is_session_owner_required
          ? this.activeEditService.is_session_owner_required
          : false,
        Validators.required,
      ],
      is_base_membership_text: [
        this.activeEditService && this.activeEditService.is_base_membership_text
          ? this.activeEditService.is_base_membership_text
          : this.mode == 'plans'
            ? null
            : 'is_base_membership_false',
        this.mode == 'plans' ? Validators.required : null,
      ],
      recurring_services: [
        this.activeEditService &&
        this.activeEditService.recurring_services == false
          ? false
          : true,
        Validators.required,
      ],
    });

    this.serviceCreateForm.addControl(
      'sessions',
      new FormControl(
        this.activeEditService && this.activeEditService.sessions
          ? this.activeEditService.sessions
          : null,
        this.CategoryId?.value &&
        this.CategoryId?.value?.behaviour == 'AppointmentPackage'
          ? Validators.required
          : null,
      ),
    );

    this.serviceCreateForm.addControl(
      'is_unlimited_sessions',
      new FormControl(
        this.activeEditService && this.activeEditService.is_unlimited_sessions
          ? this.activeEditService.is_unlimited_sessions
          : false,
        this.CategoryId?.value &&
        this.CategoryId?.value?.behaviour == 'AppointmentPackage'
          ? Validators.required
          : null,
      ),
    );

    this.serviceCreateForm.addControl(
      'service_name_primary_language',
      new FormControl(
        this.activeEditService &&
        this.activeEditService.service_name_primary_language
          ? this.activeEditService.service_name_primary_language
          : '',
        this.mode == 'addons' ? Validators.required : null,
      ),
    );

    this.serviceCreateForm.addControl(
      'service_name_secondary_language',
      new FormControl(
        this.activeEditService &&
        this.activeEditService.service_name_secondary_language
          ? this.activeEditService.service_name_secondary_language
          : '',
        this.mode == 'addons' && this.isSecondaryLanguage
          ? Validators.required
          : null,
      ),
    );

    if (
      this.subscriptionCategory &&
      (this.subscriptionCategory.key == PlanType.PaidInFull ||
        this.subscriptionCategory.key == PlanType.DropIn ||
        this.subscriptionCategory.key ==
          PlanCategory[PlanCategory.PaidInFull] ||
        this.subscriptionCategory.key == PlanCategory[PlanCategory.DropIn])
    ) {
      this.serviceCreateForm.get('recurring_services')?.setValue(false);
    } else {
      if (
        this.activeEditService &&
        this.activeEditService.recurring_services == false
      ) {
        this.serviceCreateForm.get('recurring_services')?.setValue(false);
      } else {
        this.serviceCreateForm.get('recurring_services')?.setValue(true);
      }
    }

    if (
      (this.subscriptionCategory &&
        (this.subscriptionCategory.key == PlanType.PaidInFull ||
          this.subscriptionCategory.key ==
            PlanCategory[PlanCategory.PaidInFull])) ||
      this.subscriptionCategory.key == PlanType.DropIn ||
      this.subscriptionCategory.key == PlanCategory[PlanCategory.DropIn]
    ) {
      this.serviceCreateForm.addControl(
        'interval_type',
        new FormControl(
          this.activeEditService && this.activeEditService.interval_type
            ? this.activeEditService.interval_type
            : null,
          Validators.required,
        ),
      );

      this.serviceCreateForm.addControl(
        'interval_value',
        new FormControl(
          this.activeEditService && this.activeEditService.interval_value
            ? this.activeEditService.interval_value
            : null,
          Validators.required,
        ),
      );
    } else {
      //if(this.IsRecurringServiceFormControl.value)
      // {
      this.serviceCreateForm.addControl(
        'interval_type',
        new FormControl(
          this.activeEditService && this.activeEditService.interval_type
            ? this.activeEditService.interval_type
            : null,
          !this.IsRecurringServiceFormControl.value
            ? Validators.required
            : null,
        ),
      );

      this.serviceCreateForm.addControl(
        'interval_value',
        new FormControl(
          this.activeEditService && this.activeEditService.interval_value
            ? this.activeEditService.interval_value
            : null,
          !this.IsRecurringServiceFormControl.value
            ? Validators.required
            : null,
        ),
      );
      //}
    }

    this.CategoryId?.valueChanges.subscribe({
      next: (value: any) => {
        if (value.behaviour == 'AppointmentPackage') {
          this.Sessions?.setValidators(Validators.required);
          this.Sessions?.updateValueAndValidity();
          this.Sessions?.markAsTouched();
        } else {
          this.Sessions?.removeValidators(Validators.required);
          this.Sessions?.clearValidators();
          this.Sessions?.updateValueAndValidity();
        }
        this.cd.detectChanges();
      },
    });

    this.IsUnlimitedSession?.valueChanges.subscribe({
      next: (value: any) => {
        if (value) {
          this.Sessions?.setValidators(null);
          this.Sessions?.disable({ onlySelf: true });
        } else {
          this.Sessions?.enable({ onlySelf: false });
          this.Sessions?.setValidators(Validators.required);
        }
        this.Sessions?.updateValueAndValidity();
      },
    });

    this.IsRecurringServiceFormControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value) {
          this.BillingIntervalType!.setValidators([Validators.required]);
          this.BillingIntervalValue!.setValidators([Validators.required]);
          this.BillingIntervalType!.setValue(
            this.activeEditService && this.activeEditService.interval_type
              ? this.activeEditService.interval_type
              : null,
          );
          this.BillingIntervalValue!.setValue(
            this.activeEditService && this.activeEditService.interval_type
              ? this.activeEditService.interval_type
              : null,
          );
        } else {
          this.BillingIntervalType!.clearValidators();
          this.BillingIntervalValue!.clearValidators();
          this.BillingIntervalType!.setValue(null);
          this.BillingIntervalValue!.setValue(null);
        }
      });

    this.cd.detectChanges();
  }

  initializeSubscriptionCategoryForm() {
    if (this.activeEditService && this.activeEditService.behaviour) {
      for (let [key, value] of Object.entries(MembershipBehaviour)) {
        if (key == this.activeEditService.behaviour) {
          return { name: value, key };
        }
      }
      return {
        name: MembershipBehaviour.MemberShip,
        key: 'MemberShip',
      };
    } else
      return {
        name: MembershipBehaviour.MemberShip,
        key: 'MemberShip',
      };
  }

  initializeCategoryIdForm() {
    if (
      this.activeEditService &&
      (this.activeEditService.category_id ||
        this.activeEditService.category_name)
    ) {
      if (this.type == 'bulk') {
        return this.services.filter((element: any) => {
          return (
            element.name_primary_language.toLowerCase() ==
            this.activeEditService.category_name.toLowerCase()
          );
        })[0];
      } else {
        return this.services.filter((element: any) => {
          return element.id == this.activeEditService.category_id;
        })[0];
      }
    }
    return null;
  }

  initializeTaxGroupIdForm() {
    if (this.activeEditService && this.activeEditService.taxgrp_id >= 0) {
      /// this.activeEditService.taxgrp_id){
      return this.taxGroup?.find((element: any) => {
        return element.id == this.activeEditService.taxgrp_id; ///this.activeEditService.taxgrp_id
      });
    }
    return null;
  }

  saveServiceCreateForm() {
    if (this.serviceCreateForm.valid) {
      let service = this.cleanServiceFormValues(
        this.serviceCreateForm.getRawValue(),
      );
      service.behaviour = this.services.find(
        (e) => e.id == service.category_id,
      ).behaviour;
      service.base_cost_in_minor_currency =
        this.commonService.ConvertMinorCurrencyFromAmount(
          service.base_cost_in_minor_currency,
        );

      if (service.sessions == null) {
        service.sessions = 0;
      }

      if (this.activeEditService)
        this.ref.close({ service, index: this.activeEditIndex });
      else this.ref.close({ service });
    } else {
      Object.keys(this.serviceCreateForm.controls).forEach((field) => {
        const control = this.serviceCreateForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  cleanServiceFormValues(values: any) {
    let serviceData = {
      ...values,
      category_id: values.category_id.id,
      category_name: values.category_id.name_primary_language,
      taxgrp_id: values.taxgrp_id ? values.taxgrp_id.id : null,
      name_primary_language: values.category_id.name_primary_language,
      gl_code: values.gl_code,
    };
    return serviceData;
  }

  closeDialog() {
    this.ref.close(null);
  }

  showCategoryDialog() {
    if (
      this.config.data?.planType == PlanCategory.DropIn ||
      this.config.data?.planType == PlanCategory[PlanCategory.DropIn]
    )
      this.onlyClass = true;
    this.addCategory = true;
    this.cd.detectChanges();
  }

  changedEditCategory(event: any) {
    if (event) {
      this.reloadServices(event.id);
    }
    this.addCategory = false;
  }

  setBaseMembershipLogicOnEdit() {
    if (
      this.config.data?.service.require_base_membership == true &&
      this.config.data?.service.is_base_membership == 'No'
    ) {
      this.config.data.service.is_base_membership_text =
        'require_base_membership_true';
    }
    if (
      this.config.data?.service.require_base_membership == false &&
      this.config.data?.service.is_base_membership == 'Yes'
    ) {
      this.config.data.service.is_base_membership_text =
        'is_base_membership_true';
    }
    if (
      this.config.data?.service.require_base_membership == false &&
      this.config.data?.service.is_base_membership == 'No'
    ) {
      this.config.data.service.is_base_membership_text =
        'is_base_membership_false';
    }
  }

  get BillingIntervalType() {
    return this.serviceCreateForm.get('interval_type')
      ? this.serviceCreateForm.get('interval_type')
      : null;
  }
  get BillingIntervalValue() {
    return this.serviceCreateForm.get('interval_value')
      ? this.serviceCreateForm.get('interval_value')
      : null;
  }

  public get PlanType() {
    return PlanType;
  }

  public get PlanCategory() {
    return PlanCategory;
  }

  public get PlanCategoryBehaviour() {
    return PlanCategoryBehaviour;
  }

  // Enums
  public get MembershipBehaviour() {
    return MembershipBehaviour;
  }

  get Sessions(): AbstractControl | null {
    return this.serviceCreateForm?.get('sessions')
      ? this.serviceCreateForm?.get('sessions')
      : null;
  }

  get CategoryId(): AbstractControl | null {
    return this.serviceCreateForm?.get('category_id');
  }

  get TaxGroupId(): AbstractControl | null {
    return this.serviceCreateForm?.get('taxgrp_id');
  }
  get TaxItemCode(): AbstractControl | null {
    return this.serviceCreateForm?.get('tax_item_code');
  }
  get BaseCostInMinorCurrency(): AbstractControl | null {
    return this.serviceCreateForm?.get('base_cost_in_minor_currency');
  }

  get IsBaseMembership(): AbstractControl | null {
    return this.serviceCreateForm?.get('is_base_membership_text');
  }

  get IsUnlimitedSession(): AbstractControl | null {
    return this.serviceCreateForm?.get('is_unlimited_sessions')
      ? this.serviceCreateForm?.get('is_unlimited_sessions')
      : null;
  }

  get ServiceNamePriFormControl(): AbstractControl | null {
    return this.serviceCreateForm?.get('service_name_primary_language');
  }
  get ServiceNameSecFormControl(): AbstractControl | null {
    return this.serviceCreateForm?.get('service_name_secondary_language');
  }

  get IsRecurringServiceFormControl() {
    return this.serviceCreateForm.get(
      'recurring_services',
    ) as UntypedFormControl;
  }
}
